import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { Fragment, useEffect, useState } from "react";
import { CrossIcon } from "../../assets/icons";
import LanguageSwitcher, { Language } from "../LanguageSwitcher";
import {
  AdminPlanDetailResponseAvailableDaysEnum,
  AdminPlanResponse,
  CreatePlanRequest,
  FileResponse,
  UpdatePlanRequestDietTypesEnum,
  UploadImageImageTypeEnum,
} from "../../openapi/client";
import { IDialogProps } from "../../openapi/interfaces";
import { cdnUrl, checkIf, DayList, findImageByType, maxFileSize, slugify } from "../../utils";
import Transition from "../Transition";
import useValidation from "../Validation";
import Api from "../../openapi";
import { useDispatch } from "react-redux";
import { setNotification } from "../../store/notificationSlice";
import { LoadingButton } from "@mui/lab";
import { Toggle } from "../Switch";
import { DropzoneArea } from "mui-file-dropzone";

interface IPlanCrud extends IDialogProps {
  plan?: AdminPlanResponse;
}
interface UpdatePlanRequest extends CreatePlanRequest {
  id?: string;
}

export const upload = async (imageType: UploadImageImageTypeEnum, file: File) => {
  // console.log("file", file);
  const arr = file.name.split(".");
  const extension = arr.pop();
  const fileName = arr.join(".") + new Date().toISOString() + "." + extension;
  try {
    const res = await Api.File.uploadImage(imageType, new File([file], fileName, { type: file.type }));
    // console.log(res.data);
    return res.data;
  } catch (error) {}
};

export default function Plan(props: IPlanCrud) {
  const [formDirty, setFormDirty] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<Language>("en");
  const defaultDayList = DayList.map((i) => ({ name: i, enabled: false }));
  const [plan, setPlanEdit] = useState<any>({
    name: { en: "", ar: "" },
    slug: "",
    description: { en: "", ar: "" },
    summary: { en: "", ar: "" },
    active: true,
    visible: false,
    ingredientEditable: undefined,
    minDeliveryDaysOfWeek: "",
    availableDays: [],
    dietTypes: [],
    images: [],
    portionCustomizable: false,
  });
  const [dayList, setDayList] = useState(defaultDayList);
  const dispatch = useDispatch();
  const { validation, setValidation } = useValidation(plan);
  const [imageValidation, setImageValidation] = useState({
    error: false,
    show: false,
    message: "",
    resolution: { w: 0, h: 0 },
    defaultRatio: 2,
  });
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<any>();
  const [imageRemoved, setImageRemoved] = useState(false);

  const handleFieldChange = (field: any, value?: any) => {
    setFormDirty(true);
    if (typeof field === "string") {
      if (field === "slug") {
        value = slugify(value);
      }
      setPlanEdit((state: any) => ({ ...state, [field]: value }));
    } else {
      let slugRefactor: string;
      if (field.name) {
        slugRefactor = slugify(field.name.en);
        setPlanEdit((state: any) => ({ ...state, ...field, slug: slugRefactor }));
      } else {
        setPlanEdit((state: any) => ({ ...state, ...field }));
      }
    }
  };

  const handleDayListChange = (item: string, value: boolean) => {
    setDayList((state) => {
      let temp = [...state];
      const find = temp.find((i) => i.name === item);
      find!.enabled = value;
      return temp;
    });
  };

  // console.log("plan", plan);
  // console.log("portions", portions);
  // console.log("validation", validation);

  const imageRemoval = () => {
    setImageRemoved(true);
    setImageValidation((state) => ({
      ...state,
      error: true,
      message: "No image selected for the replacement",
    }));
  };

  const fileChange = (files: any) => {
    setFiles(files);
    if (files && files.length) {
      const img = new Image();
      img.src = window.URL.createObjectURL(files[0]);
      img.onload = function () {
        const width = img.naturalWidth;
        const height = img.naturalHeight;
        const ratio = width / height;

        setImageValidation((state) => {
          let message = "";
          let error = false;
          let show = state.show;
          if (ratio > state.defaultRatio + 0.1 || ratio < state.defaultRatio - 0.1) {
            message = `Please set the width/height ratio around ${
              Math.round(state.defaultRatio * 100) / 100
            }. Currently it is ${Math.round(ratio * 100) / 100}`;
            error = true;
            show = true;
          }
          return {
            ...state,
            resolution: { w: width, h: height },
            message,
            error,
            show,
          };
        });
      };
      if (files[0].size > maxFileSize) {
        setImageValidation((state) => ({
          ...state,
          message: `Maximum image size is ${maxFileSize / 1024}KB`,
          error: true,
          show: true,
        }));
      }
    } else {
      setImageValidation((state) => ({ ...state, error: imageRemoved, show: false }));
    }
  };

  useEffect(() => {
    setPlanEdit((state: any) => ({
      ...state,
      availableDays: dayList.filter((i: any) => i.enabled).map((i: any) => i.name),
    }));
  }, [dayList]);

  // console.log(imageValidation);

  const saveChanges = async () => {
    if (imageValidation.error) {
      setImageValidation((state) => ({ ...state, show: true }));
      return;
    }

    if (validation.error) {
      setValidation((state) => ({ ...state, show: true }));
      return;
    }

    let uploadedImage: FileResponse | undefined;

    let PlanReqDto: UpdatePlanRequest = {
      ...plan,
    };

    try {
      setLoading(true);
      let message;

      if (files && files.length) {
        uploadedImage = await upload(UploadImageImageTypeEnum.Plan, files[0]);
      }
      if (uploadedImage) {
        PlanReqDto.imageId = uploadedImage.id;
      }

      if (props.create) {
        message = "Plan has been created successfully";
        await Api.Plan.createPlan(PlanReqDto);
        resetForm();
      } else if (props.edit) {
        message = "Plan has been updated successfully";
        const { id, ...UpdatePlanReqDto } = PlanReqDto;
        await Api.Plan.updatePlan(String(id), UpdatePlanReqDto);
      }
      dispatch(
        setNotification({
          show: true,
          severity: "success",
          message,
        })
      );
      props.onConfirm(true);
    } catch (error: any) {
      dispatch(
        setNotification({
          show: true,
          severity: "error",
          message: error.response?.data?.message || error.message,
        })
      );
    }
    setLoading(false);
  };

  const resetForm = () => {
    setSelectedLanguage("en");
    setPlanEdit({
      name: { en: "", ar: "" },
      slug: "",
      description: { en: "", ar: "" },
      summary: { en: "", ar: "" },
      active: true,
      visible: false,
      availableDays: undefined,
      dietTypes: [],
      minDeliveryDaysOfWeek: "",
      ingredientEditable: undefined,
      portionCustomizable: false,
    });
    setValidation((state) => ({ ...state, show: false }));
    setImageRemoved(false);
    setFiles([]);
  };

  const handlePanelClose = (...args: any) => {
    resetForm();
    props.onClose(args);
  };

  useEffect(() => {
    const getPlanById = async (planId: string) => {
      try {
        const res = await Api.Plan.getPlanById(planId);
        const plan = res.data;
        setPlanEdit({
          ...plan,
          dietTypes: plan.dietTypes ? Array.from(plan.dietTypes) : [],
        });
        setDayList(
          defaultDayList.map((li) => ({
            ...li,
            enabled: Boolean(plan.availableDays?.includes(li.name as AdminPlanDetailResponseAvailableDaysEnum)),
          }))
        );
      } catch (error) {}
    };
    if (props.plan) {
      getPlanById(String(props.plan.id));
    }
  }, [props.plan]);

  // console.log("planById", plan);
  // console.log("files", files);

  return (
    <Fragment>
      <Dialog
        open={props.open}
        TransitionComponent={Transition}
        onClose={handlePanelClose}
        aria-describedby="alert-dialog-slide-description"
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "16px",
            boxShadow: "0px 12px 24px 0px rgba(0, 0, 0, 0.15)",
            padding: "24px",
            width: "790px",
            maxWidth: "790px",
          },
          "& .MuiDialogTitle-root": {
            padding: "0 48px 16px 0",
          },
          "& .MuiDialogContent-root,& .MuiDialogActions-root": {
            padding: 0,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: "text.dark",
            fontSize: "16px",
            fontStyle: "normal",
            fontWeight: 500,
            lineHeight: "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography component="span" sx={{ fontSize: "16px", fontWeight: 500 }}>
            {props.title}
          </Typography>
          <LanguageSwitcher value={selectedLanguage} onChange={setSelectedLanguage} />
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handlePanelClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "text.fontLight2",
          }}
        >
          <CrossIcon />
        </IconButton>
        <DialogContent>
          <FormControl sx={{ width: "100%" }}>
            <Box sx={{ display: "flex", columnGap: "16px" }}>
              <Box
                className="left-image"
                sx={{
                  width: "170px",
                }}
              >
                <Box className="form-section">
                  <Typography component="span" className="form-label">
                    Thumbnail Image
                  </Typography>

                  {checkIf(findImageByType(plan.images, "THUMBNAIL")) && !imageRemoved ? (
                    <Box
                      sx={{
                        borderRadius: "12px",
                        width: "170px",
                        height: "105px",
                        background: `url(${cdnUrl + findImageByType(plan.images, "THUMBNAIL")})`,
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                        display: "flex",
                        alignItems: "flex-end",
                        justifyContent: "center",
                        paddingBottom: "12px",
                      }}
                    >
                      <Button
                        onClick={imageRemoval}
                        sx={{
                          borderRadius: "4px",
                          color: "#fff",
                          fontSize: "12px",
                          fontWeight: 500,
                          lineHeight: "13px",
                          padding: "4px",
                          backgroundColor: "text.style",
                          boxShadow: "0px 3px 6px 0px rgba(200, 38, 38, 0.25)",
                          "&:hover": { backgroundColor: "text.style" },
                        }}
                      >
                        Delete Img
                      </Button>
                    </Box>
                  ) : (
                    <Fragment>
                      <DropzoneArea
                        dropzoneClass="MuiDropzone"
                        dropzoneText="Drag files here or click to add"
                        maxFileSize={maxFileSize}
                        fileObjects={files}
                        filesLimit={1}
                        acceptedFiles={["image/*"]}
                        onDropRejected={(files) => {}}
                        onChange={fileChange}
                      />
                      {imageValidation.show && imageValidation.error ? (
                        <FormHelperText sx={{ color: "text.error", margin: 0 }}>
                          {imageValidation.message}
                        </FormHelperText>
                      ) : null}
                    </Fragment>
                  )}
                </Box>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", rowGap: "16px", flex: "1 1" }}>
                <Box className="form-section">
                  <Box sx={{ display: "flex" }}>
                    <Typography component="span" className="form-label">
                      Name {selectedLanguage === "en" ? "(English)" : "(Arabic)"}
                    </Typography>
                    <Typography component="span" className="required-label">
                      {selectedLanguage === "en" ? "*" : ""}
                    </Typography>
                  </Box>

                  <TextField
                    className="form-field"
                    value={plan?.name?.[selectedLanguage] || ""}
                    error={validation.show && validation.name?.[selectedLanguage]?.error}
                    helperText={
                      validation.show && validation.name?.[selectedLanguage]?.error
                        ? validation.name[selectedLanguage]?.helperText
                        : undefined
                    }
                    onChange={(e) =>
                      handleFieldChange({
                        name: { ...plan.name, [selectedLanguage]: e.target.value },
                      })
                    }
                    inputProps={{
                      dir: selectedLanguage === "ar" ? "rtl" : "ltr",
                    }}
                  />
                </Box>
                <Box className="form-section">
                  <Box sx={{ display: "flex" }}>
                    <Typography component="span" className="form-label">
                      Slug
                    </Typography>
                    <Typography component="span" className="required-label">
                      *
                    </Typography>
                  </Box>

                  <TextField
                    className="form-field"
                    value={plan?.slug}
                    error={validation.show && validation.slug?.error}
                    helperText={validation.show && validation.slug?.error ? validation.slug.helperText : undefined}
                    onChange={(e) => handleFieldChange("slug", e.target.value)}
                  />
                </Box>
                <Box className="form-section">
                  <Typography component="span" className="form-label">
                    Summary {selectedLanguage === "en" ? "(English)" : "(Arabic)"}
                  </Typography>
                  <TextField
                    className="form-field"
                    multiline
                    rows={2}
                    sx={{ "& .MuiInputBase-root": { height: "auto" } }}
                    value={plan?.summary?.[selectedLanguage] || ""}
                    error={validation.show && validation.summary?.[selectedLanguage]?.error}
                    helperText={
                      validation.show && validation.summary?.[selectedLanguage]?.error
                        ? validation.summary[selectedLanguage]?.helperText
                        : undefined
                    }
                    onChange={(e) =>
                      handleFieldChange({
                        summary: { ...plan.summary, [selectedLanguage]: e.target.value },
                      })
                    }
                    inputProps={{
                      dir: selectedLanguage === "ar" ? "rtl" : "ltr",
                    }}
                  />
                </Box>
                <Box className="form-section">
                  <Typography component="span" className="form-label">
                    Description {selectedLanguage === "en" ? "(English)" : "(Arabic)"}
                  </Typography>
                  <TextField
                    className="form-field"
                    multiline
                    rows={2}
                    sx={{ "& .MuiInputBase-root": { height: "auto" } }}
                    value={plan?.description?.[selectedLanguage] || ""}
                    error={validation.show && validation.description?.[selectedLanguage]?.error}
                    helperText={
                      validation.show && validation.description?.[selectedLanguage]?.error
                        ? validation.description[selectedLanguage]?.helperText
                        : undefined
                    }
                    onChange={(e) =>
                      handleFieldChange({
                        description: { ...plan.description, [selectedLanguage]: e.target.value },
                      })
                    }
                    inputProps={{
                      dir: selectedLanguage === "ar" ? "rtl" : "ltr",
                    }}
                  />
                </Box>

                <Box className="form-section">
                  <Box sx={{ display: "flex" }}>
                    <Typography component="span" className="form-label">
                      Available Days
                    </Typography>
                    <Typography component="span" className="required-label">
                      *
                    </Typography>
                  </Box>

                  <FormControl sx={{ width: "100%" }}>
                    <FormGroup sx={{ display: "flex", flexDirection: "row", columnGap: "4px" }}>
                      {dayList?.map((item) => (
                        <FormControlLabel
                          key={item.name}
                          className={item.enabled ? "selected" : ""}
                          control={
                            <Checkbox
                              checked={item.enabled}
                              onChange={(e) => handleDayListChange(item.name, e.target.checked)}
                              name={item.name}
                            />
                          }
                          label={item.name.toLowerCase()}
                          sx={{
                            "&": {
                              margin: 0,
                              padding: "12px",
                              columnGap: "4px",
                              borderRadius: "8px",
                              border: "1px solid",
                              borderColor: "text.tableColor",
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              width: "70px",
                            },
                            "&.selected": { background: "#E7F7ED", borderColor: "#E7F7ED" },
                            "& > .MuiButtonBase-root": { padding: 0 },
                            "& > .MuiTypography-root": {
                              fontSize: "10px",
                              fontWeight: 500,
                              lineHeight: "20px",
                              color: "text.dark2",
                              textTransform: "capitalize",
                            },
                            "&.selected > .MuiTypography-root": { color: "primary.main" },
                            fontSize: "18px",
                            fontWeight: 600,
                          }}
                        />
                      ))}
                    </FormGroup>
                  </FormControl>
                  {validation.show && validation.availableDays?.error ? (
                    <FormHelperText sx={{ color: "text.error", margin: 0 }}>
                      {validation.availableDays.helperText}
                    </FormHelperText>
                  ) : null}
                </Box>

                <Box className="form-section">
                  <Box sx={{ display: "flex" }}>
                    <Typography component="span" className="form-label">
                      Diet Types
                    </Typography>
                  </Box>

                  <Autocomplete
                    multiple
                    value={plan?.dietTypes || []}
                    onChange={(_, newValue) => handleFieldChange({ dietTypes: newValue })}
                    options={Object.values(UpdatePlanRequestDietTypesEnum)}
                    getOptionLabel={(option) =>
                      option
                        .split("_")
                        .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                        .join(" ")
                    }
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          {...getTagProps({ index })}
                          key={option}
                          label={option
                            .split("_")
                            .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                            .join(" ")}
                          size="small"
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        className="form-field"
                        placeholder={plan?.dietTypes?.length ? "" : "Select diet types"}
                      />
                    )}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        padding: "4px 8px",
                      },
                    }}
                  />
                </Box>

                <Box className="form-section">
                  <Box sx={{ display: "flex" }}>
                    <Typography component="span" className="form-label">
                      Minimum Number of Delivery Days per Week
                    </Typography>
                    <Typography component="span" className="required-label">
                      *
                    </Typography>
                  </Box>

                  <TextField
                    className="form-field"
                    value={plan?.minDeliveryDaysOfWeek}
                    error={validation.show && validation.minDeliveryDaysOfWeek?.error}
                    helperText={
                      validation.show && validation.minDeliveryDaysOfWeek?.error
                        ? validation.minDeliveryDaysOfWeek.helperText
                        : undefined
                    }
                    onChange={(e) => handleFieldChange({ minDeliveryDaysOfWeek: e.target.value })}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Typography sx={{ color: "text.fontLight3", fontSize: "14px", fontWeight: 500 }}>
                            days
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Box>

                <Box sx={{ display: "flex", gap: "4px" }}>
                  <Box className="form-section">
                    <FormControlLabel
                      sx={{
                        margin: 0,
                        columnGap: "8px",
                        "& .MuiFormControlLabel-label": { fontSize: "14px", fontWeight: 500 },
                      }}
                      control={
                        <Toggle
                          checked={Boolean(plan?.ingredientEditable)}
                          onChange={(e) => handleFieldChange({ ingredientEditable: Boolean(e.target.checked) })}
                        />
                      }
                      label="Make Ingredients Editable"
                    />
                  </Box>
                  <Box className="form-section">
                    <FormControlLabel
                      sx={{
                        margin: 0,
                        columnGap: "8px",
                        "& .MuiFormControlLabel-label": { fontSize: "14px", fontWeight: 500 },
                      }}
                      control={
                        <Toggle
                          checked={Boolean(plan?.active)}
                          onChange={(e) => handleFieldChange({ active: Boolean(e.target.checked) })}
                        />
                      }
                      label="Make the plan active"
                    />
                  </Box>
                </Box>

                <Box sx={{ display: "flex", gap: "4px" }}>
                  <Box className="form-section">
                    <FormControlLabel
                      sx={{
                        margin: 0,
                        columnGap: "8px",
                        "& .MuiFormControlLabel-label": { fontSize: "14px", fontWeight: 500 },
                      }}
                      control={
                        <Toggle
                          checked={Boolean(plan?.visible)}
                          onChange={(e) => handleFieldChange({ visible: Boolean(e.target.checked) })}
                        />
                      }
                      label="Make the plan visible"
                    />
                  </Box>
                  <Box className="form-section">
                    <FormControlLabel
                      sx={{
                        margin: 0,
                        columnGap: "8px",
                        "& .MuiFormControlLabel-label": { fontSize: "14px", fontWeight: 500 },
                      }}
                      control={
                        <Toggle
                          checked={Boolean(plan?.portionCustomizable)}
                          onChange={(e) => handleFieldChange({ portionCustomizable: Boolean(e.target.checked) })}
                        />
                      }
                      label="Set portions customizable"
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </FormControl>
        </DialogContent>

        <DialogActions>
          <LoadingButton
            fullWidth
            loading={loading}
            color="secondary"
            variant="contained"
            sx={{ display: "flex", flexDirection: "column", marginTop: "16px" }}
            onClick={saveChanges}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
}
